import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { createAccessRequest, type AccessResourceType } from '../packages/api-client/access-requests-api';
import { getErrorMessage } from '../packages/api-client';
import { varAlpha } from '../utils/statusColors';

/**
 * Shown when a restricted user lands on a task/project they can't see (e.g.
 * via an @-mention notification that would otherwise 404). Lets them request
 * access with an optional note; an approver is notified.
 */
export function RequestAccessCard({ resourceType, resourceId }: { resourceType: AccessResourceType; resourceId: string }) {
    const [note, setNote] = useState('');
    const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [error, setError] = useState('');

    async function submit() {
        setState('sending');
        setError('');
        try {
            await createAccessRequest(resourceType, resourceId, note);
            setState('sent');
        } catch (e) {
            setError(getErrorMessage(e));
            setState('error');
        }
    }

    const label = resourceType === 'task' ? 'task' : 'project';

    return (
        <div style={{ maxWidth: 460, margin: '4rem auto', padding: '1.5rem', textAlign: 'center', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: '2rem' }}><Lock size={32} aria-hidden="true" /></div>
            <h2 style={{ margin: '0.5rem 0', fontSize: 'var(--font-size-lg)' }}>You don't have access to this {label}</h2>
            {state === 'sent' ? (
                <p style={{ color: 'var(--color-success, #16a34a)' }}>
                    Request sent — you'll be notified when it's approved.
                </p>
            ) : (
                <>
                    <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                        Ask the {resourceType === 'task' ? "task's owner" : "project's owner"} or an admin for access.
                    </p>
                    <textarea
                        className="form-input"
                        placeholder="Add a note (optional)"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        rows={2}
                        style={{ width: '100%', margin: '0.75rem 0', resize: 'vertical' }}
                    />
                    {error && (
                        <div style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', background: varAlpha('danger', 0.12), color: 'var(--danger)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}>
                            {error}
                        </div>
                    )}
                    <button className="btn btn-primary btn-md" disabled={state === 'sending'} onClick={submit}>
                        {state === 'sending' ? 'Sending…' : 'Request access'}
                    </button>
                </>
            )}
        </div>
    );
}
